import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ShieldCheck, X } from "lucide-react";

const block = (e) => e.preventDefault();

export default function SecureDocViewer({ client, file, pageUrl, onClose }) {
  const [page, setPage] = useState(0);
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState("");
  const total = file.page_count || 1;

  useEffect(() => {
    let url = null;
    setSrc(null); setErr("");
    client.get(pageUrl(page), { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setErr("This page couldn't be loaded."));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [page, file.id]);

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c"].includes(k)) e.preventDefault();
      if (k === "arrowright") setPage((p) => Math.min(total - 1, p + 1));
      if (k === "arrowleft") setPage((p) => Math.max(0, p - 1));
      if (k === "escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex flex-col secure-viewer" data-testid="secure-viewer"
      onContextMenu={block} onCopy={block} onCut={block} onDragStart={block} style={{ userSelect: "none", WebkitUserSelect: "none" }}>
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="min-w-0">
          <div className="font-semibold truncate" data-testid="secure-viewer-title">{file.name}</div>
          <div className="text-[11px] font-mono-tight uppercase tracking-widest text-white/60 inline-flex items-center gap-1"><ShieldCheck size={12} /> Protected preview · view only</div>
        </div>
        <button data-testid="secure-viewer-close" onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-auto flex items-start justify-center px-2 pb-4">
        {err && <div className="text-white/80 mt-20 text-sm">{err}</div>}
        {!err && !src && <div className="text-white/60 mt-20 text-sm">Loading page…</div>}
        {src && (
          <div className="relative">
            <img data-testid="secure-viewer-page" src={src} alt={`Page ${page + 1}`} draggable={false}
              className="max-w-full sm:max-w-[900px] shadow-2xl pointer-events-none select-none" />
            <div className="absolute inset-0" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 pb-5 text-white">
        <button data-testid="secure-viewer-prev" disabled={page === 0} onClick={() => setPage(page - 1)} className="p-2 rounded-full bg-white/10 disabled:opacity-30"><ChevronLeft size={18} /></button>
        <span className="font-mono-tight text-sm" data-testid="secure-viewer-pageno">{page + 1} / {total}</span>
        <button data-testid="secure-viewer-next" disabled={page >= total - 1} onClick={() => setPage(page + 1)} className="p-2 rounded-full bg-white/10 disabled:opacity-30"><ChevronRight size={18} /></button>
      </div>
    </div>
  );
}
